<template>
  <div id="lots-catalog" class="site-tabs site-tabs lots-catalog box-shadow">
    <template v-if="auctionStore.lots?.length || isSearchActive">
      <div class="catalog-header">
        <div class="lots-tabs" :class="{ 'is-show-my-bids-tab': isShowMyBidsTabs }">
          <div
            v-for="tab in tabs"
            class="type-lots-tab-title tab-title"
            :class="[{ active: activeItem === tab.key }, tab.params.titleClass]"
            data-target="lot-tab-content-1"
            @click="changeTab(tab)"
            :key="tab.key">
            {{ trans(tab.name) }}
          </div>
        </div>
        <button v-if="isShowScrollBack" :class="['scroll-back-button', scrollBackBtnClass]" @click="slideToActive">
          <base-icon icon-name="arrows-double" width="14" height="14" viewBox="0 -960 960 960">
            <arrows-double-icon></arrows-double-icon>
          </base-icon>
          {{ trans("live.catalog.scroll_to_live") }}
        </button>
        <div class="lots-controllers">
          <filter-search class="search-block">
            <svg class="icon-search">
              <use href="/sprite.svg#search"></use>
            </svg>
          </filter-search>
          <div @click="openCatalogPopup" class="catalog-popup-btn">
            <div class="icon-maximize"></div>
          </div>
        </div>
      </div>
      <div class="all-lots-block tabs-content">
        <div v-for="tab in tabs" class="tab-content" :class="{ active: tab.key === activeItem }" :key="tab.key">
          <swiper
            :id="tab.key"
            :class="tab.params.class"
            :slides-per-view="tab.params.slidesPerView"
            :pagination="false"
            :navigation="state.showControls"
            :scrollbar="state.showControls"
            :mousewheel="true"
            :centered-slides="true"
            :centered-slides-bounds="true"
            :free-mode="{ enabled: true, sticky: true }"
            :type="tab.params.type"
            :customEventNavigationButtonsClick="true"
            @swiper-init="(swiper) => onSliderInit(swiper, tab.key)">
            <swiper-slide
              v-for="lot in tab.items"
              :key="lot.id"
              class="slide-lot swiper-slide"
              :data-number="lot.lot_number"
              :data-id="lot.id"
              @click="openLotDetailsPopup(lot)">
              <lot-card :lot-data="lot"></lot-card>
            </swiper-slide>
          </swiper>
          <div v-if="tab.items.length === 0 && tab.notFoundLabel" class="catalog-not-found-label">{{ trans(tab.notFoundLabel) }}</div>
          <div v-if="activeGroupLotNumbers?.length" :class="['catalog-lot-carousel', { show: state.isShowCarousel }]">
            <div v-for="item in activeGroupLotNumbers" :class="[{ active: item?.isActive }]" :key="item.id">
              {{ item.number }}
            </div>
          </div>
        </div>
      </div>
    </template>
    <Preloader v-else class="load-catalog"></Preloader>
  </div>
  <popup class="lot-details-popup full-width" ref="lotDetailsPopup"></popup>
  <catalog-popup
    class="live-catalog-modal"
    ref="popupCatalog"
    @close="state.isCatalogPopupOpen = false"></catalog-popup>
  <live-lot-alert-popup></live-lot-alert-popup>
  <auth-popup :message="trans('live.modals.auth_filter_tabs.message')" ref="authPopupRef" />
</template>

<script lang="ts" setup>
  import { computed, markRaw, nextTick, onMounted, reactive, ref, watch } from "vue";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import Popup from "@/components/_partials/Popup.vue";
  import Swiper from "@/components/_partials/Swiper.vue";
  import type { Swiper as SwiperSlider } from "swiper/types";
  import CatalogPopup from "@/components/_partials/CatalogPopup.vue";
  import LotDetailsPopup from "@/components/_partials/LotDetailsPopup.vue";
  import LotCard from "@/components/LotCard.vue";
  import { ILot } from "@/models";
  import { LotSoldStatuses, LotStatuses } from "@/enum";
  import Preloader from "@/components/_partials/Preloader.vue";
  import { ArrowsDoubleIcon, BaseIcon } from "@/components/icons";
  import FilterSearch from "@/components/_partials/catalog/FilterSearch.vue";
  import LiveLotAlertPopup from "@/components/_partials/LiveLotAlertPopup.vue";
  import apiClient from "@/services/api";
  import { trans } from "@/utils/trans";
  import { AuthPopup } from "@/components/_partials/lot-info-popup";

  const activeItem = ref<string | null>(null);
  const activeItemSlideIndex = ref<number | null>(null);
  const component = markRaw(LotDetailsPopup);
  const auctionStore = useAuctionStore();
  const lotDetailsPopup = ref<typeof Popup>();
  const popupCatalog = ref<typeof CatalogPopup>();
  const swiperMap = ref<Record<string, SwiperSlider>>({});
  const isShowScrollBack = ref(false);
  const activeGroupLotNumbers = ref([]);
  const authPopupRef = ref<HTMLDivElement>(null);
  const scrollBackBtnClass = ref<string>("");
  const activeLotId = computed(() => auctionStore.activeLot?.id ?? null);

  const catalogType = auctionStore.user?.is_admin ? "lotsLiveCatalogSeller" : "lotsLiveCatalog";
  const slidesPerView = auctionStore.user?.is_admin ? 9 : 7;

  const state = reactive({
    isShowCarousel: false,
    showCarouselTimerId: null,
    swiperParams: {
      slidesPerView: 5,
      slidesPerGroup: 5
    },
    updateParamsTimerId: null,
    showControls: true,
    isCatalogPopupOpen: false
  });

  const isSearchActive = computed(() => auctionStore.catalog.searchString && auctionStore.catalog.searchString?.length);

  const isShowMyBidsTabs = computed(() => {
    return ["my-bids", "current-bids", "won-lots", "didnt-win-lots"].includes(activeItem.value);
  });

  const auctionLots = computed(() => {
    return isSearchActive.value ? auctionStore.catalog.searchLots : auctionStore.lots;
  });

  const lotsUnsold = computed(() => {
    return (
      auctionLots.value?.filter((i: ILot) => {
        return i.sold_status === LotSoldStatuses.UNSOLD;
      }) || []
    );
  });

  const lotsSold = computed(() => {
    return (
      auctionLots.value?.filter((i: ILot) => {
        return i.sold_status === LotSoldStatuses.SOLD;
      }) || []
    );
  });

  const favoriteLots = computed(() => {
    return (
      auctionLots.value?.filter((i: ILot) => {
        return auctionStore.user?.favorite_lots?.includes(i.id);
      }) || []
    );
  });

  const lotsWithUserBid = computed(() => {
    return (
      auctionLots.value?.filter((i: ILot) => {
        return auctionStore.user?.bids && i.id in auctionStore.user.bids;
      }) || []
    );
  });

  const currentBidsList = computed(() => {
    return (
      lotsWithUserBid?.value.filter((i: ILot) => {
        return i.status === LotStatuses.PREPARED;
      }) || []
    );
  });
  const wonLotsList = computed(() => {
    return (
      lotsWithUserBid?.value.filter((i: ILot) => {
        return (
          i.status === LotStatuses.ENDED &&
          i.sold_status === LotSoldStatuses.SOLD &&
          i.leader_id === auctionStore.user?.id
        );
      }) || []
    );
  });
  const didntWonLotsList = computed(() => {
    return (
      lotsWithUserBid?.value.filter((i: ILot) => {
        return (
          i.status === LotStatuses.ENDED &&
          i.sold_status === LotSoldStatuses.SOLD &&
          i.leader_id !== auctionStore.user?.id
        );
      }) || []
    );
  });

  const lotsNotes = computed(() => {
    return (
      auctionLots.value?.filter((i: ILot) => {
        return auctionStore.user?.is_admin ? i.has_admin_notes : i.note?.length;
      }) || []
    );
  });

  const updateShowScrollBack = () => {
    let result = false;
    if (activeItem.value && activeLotId.value) {
      const swiperData = swiperMap.value[activeItem.value];
      if (swiperData) {
        let halfSlidesPV = Math.ceil(swiperData.swiper.params.slidesPerView / 2);
        halfSlidesPV = halfSlidesPV == 2 ? 3 : halfSlidesPV;
        swiperData.slides.forEach((slide, i: number) => {
          if (parseInt(slide.dataset.id) === activeLotId.value) {
            if (
              i !== activeItemSlideIndex.value &&
              ((activeItemSlideIndex.value >= halfSlidesPV && i < halfSlidesPV) || i >= halfSlidesPV)
            ) {
              result = true;
            }
          }
        });
      }
    }

    isShowScrollBack.value = result;
  };

  watch(
    () => auctionStore.activeLot,
    (nv) => {
      if (activeItem.value && nv?.id && !isShowScrollBack.value) {
        const swiperData = swiperMap.value[activeItem.value];
        if (swiperData?.swiper) {
          swiperData.slides.forEach((slide, i: number) => {
            if (parseInt(slide.dataset.id) === nv.id) {
              swiperData.swiper.slideTo(i);
            }
          });
        }
      }
    }
  );

  watch(
    () => auctionLots.value,
    () => {
      nextTick(() => {
        updateShowScrollBack();
        updateSwiperSlides();
      });
    }
  );

  watch(
    () => activeItem.value,
    () => {
      activeGroupLotNumbers.value = [];
      nextTick(() => {
        updateSwiperParams();
        updateShowScrollBack();
      });
    }
  );

  watch(
    () => isShowScrollBack.value,
    (nv) => {
      scrollBackBtnClass.value = "";
      if (nv) {
        setTimeout(() => {
          scrollBackBtnClass.value = "flashing-scroll-back";
        }, 30000);
      }
    }
  );

  const tabs = computed(() => [
    {
      key: "catalog",
      name: "live.catalog.filters.catalog",
      items: auctionLots.value,
      params: {
        class: "with-navigation-numbers lots-list",
        slidesPerView: slidesPerView,
        type: catalogType
      }
    },
    ...(!auctionStore.user?.is_admin
      ? [
          {
            key: "my-bids",
            name: "live.catalog.filters.my_bids",
            notFoundLabel: "live.catalog.filters.my_bids_not_found",
            items: lotsWithUserBid.value,
            isNeedAuth: true,
            params: {
              class: "lots-list",
              slidesPerView: slidesPerView,
              type: catalogType
            }
          }
        ]
      : []),
    ...(!auctionStore.user?.is_admin && isShowMyBidsTabs.value
      ? [
          {
            key: "current-bids",
            name: "live.catalog.filters.current",
            notFoundLabel: "live.catalog.filters.current_not_found",
            items: currentBidsList.value,
            params: {
              titleClass: "subtab-title",
              class: "lots-list",
              slidesPerView: slidesPerView,
              type: catalogType
            }
          }
        ]
      : []),
    ...(!auctionStore.user?.is_admin && isShowMyBidsTabs.value
      ? [
          {
            key: "won-lots",
            name: "live.catalog.filters.won",
            notFoundLabel: "live.catalog.filters.won_not_found",
            items: wonLotsList.value,
            params: {
              titleClass: "subtab-title",
              class: "lots-list",
              slidesPerView: slidesPerView,
              type: catalogType
            }
          }
        ]
      : []),
    ...(!auctionStore.user?.is_admin && isShowMyBidsTabs.value
      ? [
          {
            key: "didnt-win-lots",
            name: "live.catalog.filters.didnt_win",
            notFoundLabel: "live.catalog.filters.didnt_win_not_found",
            items: didntWonLotsList.value,
            params: {
              titleClass: "subtab-title",
              class: "lots-list",
              slidesPerView: slidesPerView,
              type: catalogType
            }
          }
        ]
      : []),
    //=========================
    ...(auctionStore.user?.is_admin
      ? [
          {
            key: "sold",
            name: "live.catalog.filters.sold",
            items: lotsSold.value,
            params: {
              class: "lots-list",
              slidesPerView: slidesPerView,
              type: catalogType
            }
          },
          {
            key: "unsold",
            name: "live.catalog.filters.unsold",
            items: lotsUnsold.value,
            params: {
              class: "lots-list",
              slidesPerView: slidesPerView,
              type: catalogType
            }
          }
        ]
      : []),
    {
      key: "saved-lots",
      name: auctionStore.user?.is_admin ? "live.catalog.filters.saved" : "live.catalog.filters.saved_lots",
      notFoundLabel: "live.catalog.filters.saved_lots_not_found",
      items: favoriteLots.value,
      isNeedAuth: true,
      params: {
        class: "lots-list",
        slidesPerView: slidesPerView,
        type: catalogType
      }
    },
    ...(auctionStore.user?.id
      ? [
          {
            key: "notes",
            name: "live.catalog.filters.notes",
            notFoundLabel: "live.catalog.filters.notes_not_found",
            items: lotsNotes.value,
            params: {
              class: "lots-list",
              slidesPerView: slidesPerView,
              type: catalogType
            }
          }
        ]
      : [])
  ]);

  onMounted(() => {
    activeItem.value = tabs.value[0].key;
  });

  const onSliderInit = (swiper: SwiperSlider, key: string) => {
    swiperMap.value[key] = { swiper, slides: Array.from(swiper.slidesEl.querySelectorAll("swiper-slide")) };

    swiper.on("slideChange", () => updateSwiperParams());
    swiper.on("breakpoint", () => {
      updateSwiperParams();
      updateShowScrollBack();
    });
    swiper.on("sliderMove", showCarousel);
    swiper.on("scrollbarDragMove", showCarousel);
    swiper.on("activeIndexChange", (sw) => {
      activeItemSlideIndex.value = sw.activeIndex;
      updateShowScrollBack();
    });

    if (key === "catalog" && swiperMap.value["catalog"] && activeLotId.value) {
      swiperMap.value["catalog"].slides.forEach((slide, i: number) => {
        if (parseInt(slide.dataset.id) === activeLotId.value) {
          swiperMap.value["catalog"].swiper.slideTo(i);
        }
      });
    }
  };

  const showCarousel = () => {
    state.isShowCarousel = true;

    if (state.showCarouselTimerId) {
      clearTimeout(state.showCarouselTimerId);
    }

    state.showCarouselTimerId = setTimeout(() => {
      state.isShowCarousel = false;
    }, 1000);
  };

  const updateActiveGroupLotNumbers = () => {
    if (activeItem.value && swiperMap.value && swiperMap.value[activeItem.value]?.swiper) {
      swiperMap.value[activeItem.value].slides = Array.from(swiperMap.value[activeItem.value].swiper.slidesEl.querySelectorAll("swiper-slide"))
    }
    const swiperData = swiperMap.value[activeItem.value];

    const result = new Array(Math.min(state.swiperParams.slidesPerView, swiperData?.slides.length) || 0);

    if (swiperData?.swiper && swiperData?.slides?.length) {
      const activeIndex = swiperData.swiper.activeIndex;
      const slidesPerView = state.swiperParams.slidesPerView;
      const totalSlides = swiperData.slides.length;
      const halfPerView = parseInt(state.swiperParams.slidesPerView / 2);

      const start = activeIndex >= halfPerView ? activeIndex - halfPerView : 0;
      const end = start + slidesPerView <= totalSlides ? start + slidesPerView : totalSlides;

      swiperData.slides.slice(start, end).forEach((slide, i: number) => {
        result[i] = { number: slide.dataset.number, isActive: start + i === activeIndex };
      });
    }

    activeGroupLotNumbers.value = result?.every((i) => !i?.number) ? null : result;
  };

  const updateSwiperSlides = () => {
    Object.keys(swiperMap.value).forEach((key) => {
      const swiper = swiperMap.value[key].swiper;
      swiperMap.value[key].slides =
        swiper.slidesEl && !swiper.slidesEl.destroyed
          ? Array.from(swiper.slidesEl.querySelectorAll("swiper-slide"))
          : [];
    });
    nextTick(() => {
      updateSwiperParams();
    });
  };

  const updateSwiperParams = () => {
    if (state.updateParamsTimerId) {
      clearTimeout(state.updateParamsTimerId);
      state.updateParamsTimerId = null;
    }
    state.updateParamsTimerId = setTimeout(() => {
      Object.entries(swiperMap.value).forEach(([key, sliderData]) => {
        if (key === activeItem.value && sliderData?.swiper?.params) {
          const params = swiperMap.value[activeItem.value]?.swiper.params;
          const slides = swiperMap.value[activeItem.value]?.slides;
          state.showControls = slides?.length > 0 && params.slidesPerView * params.grid.rows < slides?.length;

          nextTick(() => {
            sliderData.swiper.update();
            if (state.showControls) {
              sliderData.swiper.scrollbar.enable();
            } else {
              sliderData.swiper.scrollbar.disable();
            }
          });
          state.swiperParams.slidesPerView = sliderData.swiper.params.slidesPerView;
          state.swiperParams.slidesPerGroup = sliderData.swiper.params.slidesPerGroup;
        }
      });
      nextTick(() => updateActiveGroupLotNumbers());
    }, 50);
  };

  const slideToActive = () => {
    if (activeItem.value && activeLotId.value) {
      const swiperData = swiperMap.value[activeItem.value];
      if (swiperData?.swiper) {
        swiperData.slides.forEach((slide, i: number) => {
          if (parseInt(slide.dataset.id) === activeLotId.value) {
            activeItemSlideIndex.value = swiperData?.swiper.activeIndex;
            swiperData.swiper.slideTo(i);
            isShowScrollBack.value = false;
          }
        });
      }
    }
  };

  const changeTab = (tab) => {
    state.showControls = false;
    if (tab.isNeedAuth && !auctionStore.user?.id) {
      authPopupRef.value?.open();
    } else {
      activeItem.value = tab.key;
      nextTick(slideToActive);
    }
  };

  const openLotDetailsPopup = (lot: ILot, lotIndex?: number, isNavigation = false) => {
    // Get current tab's lots
    const currentTab = tabs.value.find(tab => tab.key === activeItem.value);
    const currentTabLots = currentTab?.items || [];
    
    // Find lot index if not provided
    const currentIndex = lotIndex !== undefined 
      ? lotIndex 
      : currentTabLots.findIndex(l => l.id === lot.id);

    apiClient.get(`/live/lots/${lot.id}/popup-data`).then((response) => {
      const componentProps = {
        lotProp: Object.assign(lot, response.data),
        allLots: currentTabLots,
        currentLotIndex: currentIndex,
        onNavigateToLot: (nextLot: ILot, nextIndex: number) => {
          openLotDetailsPopup(nextLot, nextIndex, true);
        }
      };
      
      lotDetailsPopup.value?.toggleComponent(component, componentProps);
      if (!isNavigation) {
        lotDetailsPopup.value?.open();
      }
    });
  };

  const openCatalogPopup = () => {
    popupCatalog.value?.open();
    state.isCatalogPopupOpen = true;
  };
</script>
<style scoped>
  .scroll-back-button.flashing-scroll-back {
    animation: flashingScrollBackBtn 0.3s infinite alternate;
  }
  @keyframes flashingScrollBackBtn {
    from {
      opacity: 1;
    }
    to {
      opacity: 0.5;
    }
  }
</style>
